import { useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PermIdentityOutlinedIcon from '@mui/icons-material/PermIdentityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import { useLang } from '@spa/i18n/useLang';
import { useAppSelector } from '@/store';
import { SpaRoutes } from '@spa/constants/routes';
import type { TranslationKey } from '@spa/types/lang.types';

interface MenuItemConfig {
  key: string;
  labelId: TranslationKey;
  icon: React.ReactNode;
}

const MENU_ITEMS: MenuItemConfig[] = [
  {
    key: SpaRoutes.ACCOUNT_SETTINGS,
    labelId: 'global.accountSettings',
    icon: <EditOutlinedIcon />,
  },
  {
    key: SpaRoutes.LOGOUT,
    labelId: 'global.logout',
    icon: <LogoutOutlinedIcon />,
  },
];

export function AccountInformation() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const t = useLang();
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  // @Todo: legacy tech debt
  const { firstName, lastName } = useAppSelector(
    ({ company }) => company.customer
  );

  const ariaLabel =
    `${firstName} ${lastName}`.trim() || t('global.accountSettings');

  const onToggle = () => setIsOpen((prev) => !prev);
  const onClose = () => setIsOpen(false);

  const handleItemClick = (item: MenuItemConfig) => {
    navigate(item.key);
    onClose();
  };

  return (
    <div
      ref={anchorRef}
      className={`relative inline-flex bg-white rounded-md border-1 transition-colors duration-150 ${
        isOpen
          ? 'border-[var(--color-primary-text)] z-[110]'
          : 'border-[var(--color-biomar-border)]'
      }`}
    >
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="true"
        aria-expanded={isOpen}
        onClick={onToggle}
        className="!p-1.5 md:!p-3"
      >
        <PermIdentityOutlinedIcon
          sx={{ fontSize: '24px', color: 'var(--color-primary-text)' }}
        />
      </button>

      {isOpen && (
        <div
          className="fixed inset-0 z-[100]"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <div
        className={`
            absolute left-0 z-[110] top-full mt-3 md:left-auto md:right-0
            min-w-[200px] rounded-md bg-white
            shadow-[var(--shadow-dropdown)]
            transition-all duration-150 ease-out
            ${
              isOpen
                ? 'opacity-100 translate-y-0 pointer-events-auto'
                : 'opacity-0 -translate-y-2 pointer-events-none'
            }
          `}
      >
        <ul role="menu" className="!m-0">
          {MENU_ITEMS.map((item) => (
            <li key={item.key} role="none" className="!p-0">
              <button
                type="button"
                role="menuitem"
                onClick={() => handleItemClick(item)}
                className={`
                    flex w-full items-center gap-3
                    px-4 py-4
                    text-sm text-[var(--color-primary-text)]
                    hover:bg-gray-100
                    transition-colors duration-100
                    ${
                      pathname === item.key
                        ? 'bg-gray-100 !font-semibold'
                        : '!font-normal'
                    }
                  `}
              >
                <span>{item.icon}</span>
                {t(item.labelId)}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default AccountInformation;
